import { Skeleton } from "@/components/ui/skeleton";
import { TooltipContent, TooltipProvider, TooltipTrigger, Tooltip as UITooltip } from "@/components/ui/tooltip";
import { useDashboard } from "@/contexts/DashboardContext";
import { formatTooltipTime, formatXAxisTick, GroupByType } from "@/utils/dateUtils";
import { CALL_VOLUME_COLORS } from "@/constants/colors";
import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { Info } from "lucide-react";
import { LEGEND_WRAPPER_STYLE } from "@/lib/chartConfig";

interface CallVolumeWidgetData {
  time: Date;
  inboundDirectCall: number;
  internalCall: number;
  outboundCall: number;
}

/**
 *
 * @param dateRangeType
 * @param dateRange
 *
 * - When dateRange is the same day, return "hour"
 * - When dateRange is the same month, return "day"
 * - When dateRange is the same year, return "month"
 * - When dateRange is not the same year, return "day"
 *
 * @returns
 *
 */
const getGroupByFromDateRange = (dateRangeType: string, dateRange?: { from: Date, to: Date }): GroupByType => {
  if (!dateRange) return "day";

  const isDateRangeTheSameYear = dateRange.from.getFullYear() === dateRange.to.getFullYear();
  const isDateRangeTheSameMonth = dateRange.from.getMonth() === dateRange.to.getMonth() && isDateRangeTheSameYear;
  const isDateRangeTheSameDay = dateRange.from.getDate() === dateRange.to.getDate() && isDateRangeTheSameMonth && isDateRangeTheSameYear;

  if (isDateRangeTheSameDay) {
    return "hour";
  } else if (isDateRangeTheSameYear) {
    return "day";
  } else {
    return "daydiffyear";
  }
};

function getEffectiveDateRange(
  processedData: CallVolumeWidgetData[] | undefined,
  fallbackDateRange: { from: Date; to: Date }
): { from: Date; to: Date } {
  if (!processedData || processedData.length === 0) return fallbackDateRange;
  const times = processedData.map(item => item.time.getTime());
  return { from: new Date(Math.min(...times)), to: new Date(Math.max(...times)) };
}

// Build placeholder buckets (0 calls) spanning dateRange so axes still render when there's no data
function generateEmptyBuckets(dateRange: { from: Date, to: Date }, groupBy: GroupByType): CallVolumeWidgetData[] {
  const buckets: CallVolumeWidgetData[] = [];
  const cursor = new Date(dateRange.from);

  if (groupBy === 'hour') {
    cursor.setMinutes(0, 0, 0);
    for (let i = 0; i < 24; i++) {
      buckets.push({ time: new Date(cursor), inboundDirectCall: 0, internalCall: 0, outboundCall: 0 });
      cursor.setHours(cursor.getHours() + 1);
    }
    return buckets;
  }

  cursor.setHours(0, 0, 0, 0);
  const end = new Date(dateRange.to);
  end.setHours(0, 0, 0, 0);
  while (cursor.getTime() <= end.getTime()) {
    buckets.push({ time: new Date(cursor), inboundDirectCall: 0, internalCall: 0, outboundCall: 0 });
    cursor.setDate(cursor.getDate() + 1);
  }
  return buckets;
}

function isAllCallCountsZero(processedData?: CallVolumeWidgetData[]): boolean {
  if (!processedData) return false;
  return processedData.every(item =>
    item.inboundDirectCall === 0 && item.internalCall === 0 && item.outboundCall === 0
  );
}

function computeTickGap(
  container: (HTMLDivElement & { container?: { clientWidth?: number } }) | null,
  itemCount: number,
  groupBy: GroupByType
): number | null {
  if (!container || itemCount === 0) return null;
  const width = (container.container?.clientWidth || container.offsetWidth || 600) - 60 - 25;
  const required = groupBy === 'daydiffyear' ? 80 : 60;
  return Math.ceil(required / (width / itemCount));
}

function getCallVolumeColors(theme?: string) {
  const isDark = theme === 'dark';
  return {
    inboundDirect: isDark ? CALL_VOLUME_COLORS.INBOUND_DIRECT.dark : CALL_VOLUME_COLORS.INBOUND_DIRECT.light,
    internal: isDark ? CALL_VOLUME_COLORS.INTERNAL.dark : CALL_VOLUME_COLORS.INTERNAL.light,
    outbound: isDark ? CALL_VOLUME_COLORS.OUTBOUND.dark : CALL_VOLUME_COLORS.OUTBOUND.light,
  };
}

function getVolumeXAxisTicks(processedData: CallVolumeWidgetData[] | undefined, tickGap: number): Date[] | undefined {
  if (!processedData) return undefined;
  if (processedData.length <= tickGap) return processedData.map(d => d.time);
  return Array.from({ length: Math.ceil(processedData.length / tickGap) }, (_, i) => processedData[i * tickGap]?.time)
    .filter((time): time is Date => Boolean(time));
}

function getZeroDataYAxisConfig(): { domain: [number, number]; ticks: number[] } {
  return { domain: [0, 10], ticks: [0, 2, 4, 6, 8, 10] };
}

function getStandardYAxisConfig(): { domain?: [number, number]; ticks?: number[] } {
  return {};
}

function getTooltipCursorFill(theme?: string): string {
  return theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)";
}

function formatVolumeLegendLabel(value: string, colors: { inboundDirect: string; internal: string; outbound: string }) {
  let color: string | undefined;
  if (value === 'Inbound Direct') color = colors.inboundDirect;
  else if (value === 'Internal') color = colors.internal;
  else if (value === 'Outbound') color = colors.outbound;
  return <span style={{ color }}>{value}</span>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomTooltip = ({ active, payload, label, theme, groupBy }: any) => {
  if (active && payload && payload.length) {
    const currentInboundDirectColor = theme === 'dark' ? CALL_VOLUME_COLORS.INBOUND_DIRECT.dark : CALL_VOLUME_COLORS.INBOUND_DIRECT.light;
    const currentInternalColor = theme === 'dark' ? CALL_VOLUME_COLORS.INTERNAL.dark : CALL_VOLUME_COLORS.INTERNAL.light;
    const currentOutboundColor = theme === 'dark' ? CALL_VOLUME_COLORS.OUTBOUND.dark : CALL_VOLUME_COLORS.OUTBOUND.light;

    return (
      <div className="bg-white dark:bg-gray-800 p-3 border dark:border-gray-600 rounded shadow-lg">
        <p className="font-semibold dark:text-white">{formatTooltipTime(label, groupBy)}</p>
        <p style={{ color: currentOutboundColor }}>Outbound: {payload[0].value}</p>
        <p style={{ color: currentInboundDirectColor }}>Inbound Direct: {payload[1].value}</p>
        <p style={{ color: currentInternalColor }}>Internal: {payload[2].value}</p>
      </div>
    );
  }
  return null;
};

const CallVolumeDistributionWidget: React.FC<{ data?: { callOverviewData?: { callVolumeDistribution?: CallVolumeWidgetData[] } }; dateRangeType: string, dateRange: { from: Date, to: Date }, icon?: React.ReactNode; loading?: boolean; error?: string }> = ({ data, dateRangeType, dateRange, loading }) => {
  const { theme } = useDashboard();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [tickGap, setTickGap] = useState<number>(1);

  // Convert data to local timezone and group by month if needed
  const callVolumeData = data?.callOverviewData?.callVolumeDistribution || [];
  const processedData = callVolumeData?.map(item => ({
    ...item,
    time: new Date(item.time)
  }));

  const effectiveDateRange = getEffectiveDateRange(processedData, dateRange);
  const groupBy: GroupByType = getGroupByFromDateRange(dateRangeType, effectiveDateRange);
  // No data at all: show empty axes (still 0..10 scale) instead of a "no data" message
  const chartData = processedData?.length ? processedData : generateEmptyBuckets(effectiveDateRange, groupBy);
  const isAllDataZero = isAllCallCountsZero(chartData);

  // Calculate tick gap based on container width and data length
  useEffect(() => {
    const gap = computeTickGap(containerRef.current, chartData?.length ?? 0, groupBy);
    if (gap !== null) setTickGap(gap);
  }, [groupBy, chartData]);

  const colors = getCallVolumeColors(theme);
  const { domain: yAxisDomain, ticks: yAxisTicks } = isAllDataZero ? getZeroDataYAxisConfig() : getStandardYAxisConfig();

  let chartContent: React.ReactNode;
  if (loading) {
    chartContent = <Skeleton className="h-full w-full my-3" />;
  } else {
    chartContent = (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="time"
            tick={{ fontSize: 14, fill: 'currentColor' }}
            tickFormatter={(value) => formatXAxisTick(value, groupBy)}
            interval={0}
            stroke="currentColor"
            ticks={getVolumeXAxisTicks(chartData, tickGap)}
          />
          <YAxis
            tick={{ fontSize: 14, fill: 'currentColor' }}
            stroke="currentColor"
            domain={yAxisDomain}
            ticks={yAxisTicks}
            allowDecimals={false}
          />
          <Tooltip content={(<CustomTooltip theme={theme} groupBy={groupBy} />)} cursor={{ fill: getTooltipCursorFill(theme) }} />
          <Legend
            wrapperStyle={LEGEND_WRAPPER_STYLE}
            verticalAlign="top"
            align="center"
            iconType="rect"
            formatter={(value) => formatVolumeLegendLabel(value, colors)}
          />
          <Bar dataKey="outboundCall" name="Outbound" fill={colors.outbound} radius={[4, 4, 0, 0]} maxBarSize={24} />
          <Bar dataKey="inboundDirectCall" name="Inbound Direct" fill={colors.inboundDirect} radius={[4, 4, 0, 0]} maxBarSize={24} />
          <Bar dataKey="internalCall" name="Internal" fill={colors.internal} radius={[4, 4, 0, 0]} maxBarSize={24} />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  return (
    <div className="w-full min-w-[200px] h-full rounded-lg border flex flex-col bg-white dark:bg-[#0C3451] dark:text-white dark:border-[#0C3451] p-3">
      <div ref={containerRef} className="flex flex-row items-center gap-2">
        <div className="text-[14px] font-semibold text-primary dark:text-white truncate">Call Volume Distribution</div>
        <TooltipProvider>
          <UITooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span className="cursor-pointer">
                <Info className="h-4 w-4 text-primary dark:text-white" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="max-w-xs text-xs">The total number of inbound direct calls, outbound calls and internal calls within the given time.</p>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>
      </div>
      <div className="flex-1 min-h-[260px] flex flex-col">
        {chartContent}
      </div>
    </div>
  );
};

export default CallVolumeDistributionWidget;
